import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import EmployeeSelector from '../../../events/EmployeeSelector'
import { statusChip } from './Discrepancies'

const DiscrepancyPage = () => {
  const { discrepancyID } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [discrepancy, setDiscrepancy] = useState(null)

  const [note, setNote] = useState('')
  const [newStatus, setNewStatus] = useState('')
  const [assignee, setAssignee] = useState(null)
  const [saving, setSaving] = useState(false)
  const [resolveOpen, setResolveOpen] = useState(false)
  const [resolutionNote, setResolutionNote] = useState('')

  useEffect(() => {
    API.get('/medsmx/discrepancies/' + discrepancyID)
      .then((res) => {
        if (res.status === 200) {
          setDiscrepancy(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [discrepancyID])

  const resolved = discrepancy?.status === 'Resolved'

  const postUpdate = (payload, onDone) => {
    setSaving(true)
    API.post(`/medsmx/discrepancies/${discrepancyID}/update`, payload)
      .then((res) => {
        if (res.status === 200) {
          setDiscrepancy(res.data)
          setNote('')
          setNewStatus('')
          onDone?.()
        } else {
          setToast({
            message: res.data?.error ?? 'Unable to update the discrepancy.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({
          message: 'Unable to update the discrepancy.',
          severity: 'error',
        }),
      )
      .finally(() => setSaving(false))
  }

  const raiseIncident = () => {
    setSaving(true)
    API.post(`/medsmx/discrepancies/${discrepancyID}/raise-incident`)
      .then((res) => {
        if (res.status === 200) {
          navigate(`/incidents/${res.data.incidentID}`)
        } else {
          setToast({
            message: res.data?.error ?? 'Unable to raise an incident.',
            severity: 'error',
          })
        }
      })
      .catch(() =>
        setToast({ message: 'Unable to raise an incident.', severity: 'error' }),
      )
      .finally(() => setSaving(false))
  }

  const where =
    discrepancy?.location?.name ??
    [
      discrepancy?.employee?.userAccount?.firstName,
      discrepancy?.employee?.userAccount?.lastName,
    ]
      .filter(Boolean)
      .join(' ')

  return (
    <Stack component={Paper} padding={1} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="title2">Medicine Discrepancy</Typography>
        {discrepancy && statusChip(discrepancy.status)}
      </Stack>
      {loading ? (
        <LinearProgress />
      ) : fetchError ? (
        <Alert severity="error">
          <AlertTitle>Unable to fetch Discrepancy</AlertTitle>Please try again
          later.
        </Alert>
      ) : (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Raised"
                value={moment(discrepancy.createdAt).format('Do MMM YYYY HH:mm')}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Found During"
                value={
                  discrepancy.source === 'cdAudit'
                    ? 'Controlled drug audit'
                    : 'Bag audit'
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow label="Where" value={where} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <DetailRow
                label="Assigned To"
                value={
                  [
                    discrepancy.assignedTo?.firstName,
                    discrepancy.assignedTo?.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ') || 'Unassigned'
                }
              />
            </Grid>
          </Grid>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Medicine</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell align="right">Expected</TableCell>
                <TableCell align="right">Found</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(discrepancy.details ?? []).map((detail, index) => (
                <TableRow key={index}>
                  <TableCell>{detail.title}</TableCell>
                  <TableCell>{detail.batch ?? '—'}</TableCell>
                  <TableCell align="right">{detail.expected}</TableCell>
                  <TableCell align="right">{detail.actual}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" gap={1} flexWrap="wrap">
            {discrepancy.audit && (
              <Button
                size="small"
                onClick={() =>
                  navigate(`/medicines-management/audits/${discrepancy.audit._id ?? discrepancy.audit}`)
                }
              >
                View Bag Audit
              </Button>
            )}
            {discrepancy.cdAudit && (
              <Button
                size="small"
                onClick={() =>
                  navigate(`/cd-audit/${discrepancy.cdAudit._id ?? discrepancy.cdAudit}`)
                }
              >
                View CD Audit
              </Button>
            )}
            {discrepancy.incident ? (
              <Button
                size="small"
                onClick={() => navigate(`/incidents/${discrepancy.incident._id}`)}
              >
                {`View Incident #${discrepancy.incident.incidentNumber}`}
              </Button>
            ) : (
              <Button size="small" disabled={saving} onClick={raiseIncident}>
                Raise Incident
              </Button>
            )}
          </Stack>

          <Divider />
          <Typography variant="title3">Updates</Typography>
          {(discrepancy.updates ?? []).length === 0 && (
            <Typography color="text.secondary" variant="body2">
              No updates yet.
            </Typography>
          )}
          {(discrepancy.updates ?? []).map((update, index) => (
            <Stack key={index} direction="row" gap={1} alignItems="baseline">
              <Typography variant="caption" color="text.secondary" minWidth={120}>
                {moment(update.at).format('DD/MM/YY HH:mm')}
              </Typography>
              <Typography variant="body2">
                <b>
                  {[update.by?.firstName, update.by?.lastName]
                    .filter(Boolean)
                    .join(' ')}
                </b>{' '}
                {update.statusChange && `changed the status to ${update.statusChange}. `}
                {update.note}
              </Typography>
            </Stack>
          ))}
          {discrepancy.resolutionNote && (
            <Alert severity="success">
              <AlertTitle>Resolved</AlertTitle>
              {discrepancy.resolutionNote}
            </Alert>
          )}

          {!resolved && (
            <>
              <Divider />
              <Typography variant="title3">Add an Update</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Status"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <MenuItem value="">No change</MenuItem>
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="Under Investigation">
                      Under Investigation
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <EmployeeSelector
                    label="Assign To"
                    size="small"
                    value={assignee}
                    onChange={(e, newValue) => setAssignee(newValue)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" gap={1}>
                    <Button
                      variant="contained"
                      disabled={saving || (!note.trim() && !newStatus && !assignee)}
                      onClick={() =>
                        postUpdate({
                          note,
                          status: newStatus || undefined,
                          ...(assignee && { assignedTo: assignee._id }),
                        })
                      }
                    >
                      Save Update
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      disabled={saving}
                      onClick={() => setResolveOpen(true)}
                    >
                      Resolve
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} fullWidth>
        <DialogTitle>Resolve Discrepancy</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            required
            multiline
            minRows={2}
            margin="dense"
            label="Resolution note"
            helperText="What explained the mismatch, and what was done about it"
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={saving || !resolutionNote.trim()}
            onClick={() =>
              postUpdate({ note: resolutionNote, status: 'Resolved' }, () =>
                setResolveOpen(false),
              )
            }
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default DiscrepancyPage
